import { Popover, Box, Typography, Checkbox, ListItemText, Theme, Button } from "@mui/material";
import { FilterOptionsPill } from "components/FilterOptionsPill";
import ClearIcon from "@mui/icons-material/Clear";
import { IMultiFilters } from "../../BehaviouralComparison.types";

const FiltersPopover = ({
    anchorEl,
    handleClose,
    multiFilterData,
    setMultiFilterData,
}: {
    anchorEl: HTMLElement | null;
    handleClose: () => void;
    multiFilterData: IMultiFilters;
    setMultiFilterData: React.Dispatch<React.SetStateAction<IMultiFilters>>;
}) => {
    const open = Boolean(anchorEl);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, type: string, itemId: number) => {
        const isChecked = event.target.checked;
        setMultiFilterData((prevState) => {
            if (type === "teams") {
                return {
                    ...prevState,
                    teams: prevState.teams.map((team) => (team.id === itemId ? { ...team, checked: isChecked } : team)),
                };
            } else if (type === "functions") {
                return {
                    ...prevState,
                    functions: prevState.functions.map((func) =>
                        func.id === itemId ? { ...func, checked: isChecked } : func
                    ),
                };
            } else if (type === "hierarchies") {
                return {
                    ...prevState,
                    hierarchies: prevState.hierarchies.map((hier) =>
                        hier.id === itemId ? { ...hier, checked: isChecked } : hier
                    ),
                };
            }
            return prevState;
        });
    };

    const handleDeselectAll = () => {
        setMultiFilterData((prevState) => ({
            ...prevState,
            teams: prevState.teams.map((item) => ({ ...item, checked: false })),
            functions: prevState.functions.map((item) => ({ ...item, checked: false })),
            hierarchies: prevState.hierarchies.map((item) => ({ ...item, checked: false })),
        }));
    };

    return (
        <Popover
            id="filters-popover"
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            PaperProps={{
                sx: (theme: Theme) => ({
                    border: `1px solid ${theme.palette.secondary.light}`,
                    borderRadius: "30px",
                }),
            }}
        >
            <Box padding={2}>
                <Box sx={{ display: "flex", padding: 2 }}>
                    <Box sx={{ mr: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={(theme: Theme) => ({ marginBottom: "8px", color: theme.palette.text.disabled })}
                        >
                            Teams
                        </Typography>

                        {multiFilterData.teams.map((team) => (
                            <Box key={team.id} display="flex" alignItems="center">
                                <Checkbox
                                    checked={team.checked}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={(theme: Theme) => ({
                                        padding: theme.spacing(0.5),
                                        "& > .MuiSvgIcon-root": {
                                            fontSize: 16,
                                        },
                                    })}
                                    onChange={(ev) => handleCheckboxChange(ev, "teams", team.id)}
                                />

                                <ListItemText
                                    primary={
                                        <FilterOptionsPill
                                            filterId="team"
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                                                    <Typography noWrap fontSize={12}>
                                                        {team.name}
                                                    </Typography>
                                                    <Box
                                                        sx={(theme: Theme) => ({
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "40px",
                                                            backgroundColor: theme.palette.info.main,
                                                            color: theme.palette.common.white,
                                                            fontSize: 8,
                                                            fontWeight: 600,
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            marginLeft: theme.spacing(0.5),
                                                            flex: "0 0 auto",
                                                        })}
                                                    >
                                                        3
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    }
                                />
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ mr: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={(theme: Theme) => ({ marginBottom: "8px", color: theme.palette.text.disabled })}
                        >
                            Functions
                        </Typography>

                        {multiFilterData.functions.map((func) => (
                            <Box key={func.id} display="flex" alignItems="center">
                                <Checkbox
                                    checked={func.checked}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={(theme: Theme) => ({
                                        padding: theme.spacing(0.5),
                                        "& > .MuiSvgIcon-root": {
                                            fontSize: 16,
                                        },
                                    })}
                                    onChange={(ev) => handleCheckboxChange(ev, "functions", func.id)}
                                />

                                <ListItemText
                                    primary={
                                        <FilterOptionsPill
                                            filterId="function"
                                            type={func.label}
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                                                    <Typography noWrap fontSize={12}>
                                                        {func.name}
                                                    </Typography>
                                                    <Box
                                                        sx={(theme: Theme) => ({
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "40px",
                                                            backgroundColor: theme.palette.info.main,
                                                            color: theme.palette.common.white,
                                                            fontSize: 8,
                                                            fontWeight: 600,
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            marginLeft: theme.spacing(0.5),
                                                            flex: "0 0 auto",
                                                        })}
                                                    >
                                                        3
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    }
                                />
                            </Box>
                        ))}
                    </Box>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={(theme: Theme) => ({ marginBottom: "8px", color: theme.palette.text.disabled })}
                        >
                            Hierarchies
                        </Typography>

                        {multiFilterData.hierarchies.map((hier) => (
                            <Box key={hier.id} display="flex" alignItems="center">
                                <Checkbox
                                    checked={hier.checked}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={(theme: Theme) => ({
                                        padding: theme.spacing(0.5),
                                        "& > .MuiSvgIcon-root": {
                                            fontSize: 16,
                                        },
                                    })}
                                    onChange={(ev) => handleCheckboxChange(ev, "hierarchies", hier.id)}
                                />

                                <ListItemText
                                    primary={
                                        <FilterOptionsPill
                                            filterId="hierarchy"
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                                                    <Typography noWrap fontSize={12}>
                                                        {hier.name}
                                                    </Typography>
                                                    <Box
                                                        sx={(theme: Theme) => ({
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "40px",
                                                            backgroundColor: theme.palette.info.main,
                                                            color: theme.palette.common.white,
                                                            fontSize: 8,
                                                            fontWeight: 600,
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            marginLeft: theme.spacing(0.5),
                                                            flex: "0 0 auto",
                                                        })}
                                                    >
                                                        3
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    }
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box display="flex" justifyContent="end">
                    <Button
                        size="small"
                        variant="text"
                        sx={(theme: Theme) => ({
                            fontSize: 12,
                            color: theme.palette.text.disabled,
                            padding: theme.spacing(0, 1, 0, 0),
                            minWidth: "auto",
                            "& .MuiButton-startIcon": {
                                marginRight: "4px",
                            },
                        })}
                        startIcon={<ClearIcon fontSize="small" />}
                        onClick={handleDeselectAll}
                    >
                        <Typography fontSize={12}>Clear All Filters</Typography>
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};

export default FiltersPopover;
